/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-09 20:15:10
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-07-10 14:52:44
 */
import { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react'

// 整改记录类型
interface RectifyRecord {
  recordID: number | string
  [key: string]: any
}

// 选择ID类型
type SelectionId = number | string

/**
 * 管理表格行选择逻辑的Hook
 * @returns {Object} 选择相关的状态和方法
 */
const useSelection = () => {
  // 跨页选择状态管理
  const [selectedIds, setSelectedIds] = useState<SelectionId[]>([]) // 所有已选择的ID（跨页）
  const [currentPageKeys, setCurrentPageKeys] = useState<SelectionId[]>([]) // 当前页显示的选中行

  // 添加ref来存储最新的selectedIds，解决闭包问题
  const selectedIdsRef = useRef(selectedIds)

  // 当selectedIds变化时更新ref
  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  /**
   * 更新当前页面的选中状态
   * @param {Array} records - 当前页面的记录
   */
  const updateCurrentPageSelection = useCallback(
    (records: RectifyRecord[]) => {
      if (!records || records.length === 0) {
        setCurrentPageKeys([])
        return
      }

      // 获取当前页的所有ID
      const currentPageIds = records.map((record) => record.recordID)

      // 计算当前页面上应该显示为选中状态的行
      const selectedKeysInCurrentPage = currentPageIds.filter((id) =>
        selectedIdsRef.current.includes(id)
      )
      setCurrentPageKeys(selectedKeysInCurrentPage)
    },
    [selectedIds]
  )

  /**
   * 处理行选择变化
   * @param {Array} newSelectedRowKeys - 当前页面选中的行键
   * @param {Array} records - 当前页面的记录
   */
  const handleRowSelectionChange = useCallback(
    (newSelectedRowKeys: SelectionId[], records: RectifyRecord[]) => {
      // 获取当前页的所有ID
      const currentPageIds = records.map((record) => record.recordID)

      // 计算新增的选中项和取消选中的项
      const newlySelected = newSelectedRowKeys.filter(
        (id) => !selectedIds.includes(id)
      )
      const newlyUnselected = currentPageIds.filter(
        (id) => selectedIds.includes(id) && !newSelectedRowKeys.includes(id)
      )

      // 更新总的选中状态
      setSelectedIds((prevSelectedIds) => {
        const updatedIds = [...prevSelectedIds]

        // 添加新选中的ID
        newlySelected.forEach((id) => {
          if (!updatedIds.includes(id)) {
            updatedIds.push(id)
          }
        })

        // 移除取消选中的ID
        return updatedIds.filter((id) => !newlyUnselected.includes(id))
      })

      // 更新当前页显示
      setCurrentPageKeys(newSelectedRowKeys)
    },
    [selectedIds]
  )

  /**
   * 处理全选按钮点击
   * @param {Event} e - 点击事件
   * @param {Array} records - 当前页面的记录
   */
  const handleSelectAllClick = useCallback((e: ChangeEvent<HTMLInputElement>, records: RectifyRecord[]) => {
    const shouldSelectAll = e.target.checked
    const currentPageIds = records.map((item) => item.recordID)

    if (shouldSelectAll) {
      // 全选当前页面
      setSelectedIds((prevSelectedIds) => {
        const updatedIds = [...prevSelectedIds]

        // 添加当前页所有ID（如果不存在）
        currentPageIds.forEach((id) => {
          if (!updatedIds.includes(id)) {
            updatedIds.push(id)
          }
        })

        return updatedIds
      })

      setCurrentPageKeys(currentPageIds)
    } else {
      // 取消选中当前页面的所有行
      setSelectedIds((prevSelectedIds) =>
        prevSelectedIds.filter((id) => !currentPageIds.includes(id))
      )

      setCurrentPageKeys([])
    }
  }, [])

  /**
   * 检查当前页是否全选
   * @param {Array} records - 当前页面的记录
   * @returns {boolean} 是否全选
   */
  const isAllSelected = useCallback(
    (records: RectifyRecord[]) => {
      if (!records || records.length === 0) return false

      // 检查当前页是否所有行都被选中
      const currentPageIds = records.map((record) => record.recordID)
      return (
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => currentPageKeys.includes(id))
      )
    },
    [currentPageKeys]
  )

  /**
   * 重置选择状态
   */
  const resetSelection = useCallback(() => {
    setSelectedIds([])
    setCurrentPageKeys([])
  }, [])

  /**
   * 从选中项中移除指定ID
   * @param {string|number} id - 要移除的ID
   */
  const removeFromSelection = useCallback((id: SelectionId) => {
    setSelectedIds((prevIds) => prevIds.filter((prevId) => prevId !== id))
    setCurrentPageKeys((prevKeys) => prevKeys.filter((key) => key !== id))
  }, [])

  return {
    // 状态
    selectedIds,
    currentPageKeys,
    // 方法
    updateCurrentPageSelection,
    handleRowSelectionChange,
    handleSelectAllClick,
    isAllSelected,
    resetSelection,
    removeFromSelection
  }
}

export default useSelection
